/**
 * QuickActionsSheet - Sheet lateral de Ações Rápidas
 *
 * Atalho visual (botão de raio ao lado do input do chat) que abre um Sheet
 * lateral listando todas as Ações Rápidas (regras manuais) disponíveis ao
 * usuário, com campo de busca e agrupamento por categoria.
 *
 * Cada item expõe 3 controles equivalentes aos da seção lateral do chat:
 *   - Olho (Eye): abre RulePreviewDialog para visualizar as ações da regra
 *   - Play: executa a regra via edge function `routing-engine`
 *   - Lápis (Pencil): abre EditRuleDialog (apenas quando is_owner=true)
 *
 * v1.4.0: Ordenação PESSOAL (por usuário) via drag-and-drop.
 *   - Handle GripVertical arrasta categorias (entre si) e ações (dentro do
 *     mesmo grupo, para não alterar routing_rules.category_id que é global).
 *   - Persistência nas RPCs upsert_user_quick_action_order e
 *     upsert_user_quick_action_category_order (tabelas *_user_order).
 *   - A ordem pessoal só afeta o próprio usuário; se não houver override,
 *     usa fallback global (routing_rules.priority /
 *     routing_rule_categories.sort_order).
 *   - Drag desabilitado quando há busca ativa (evita confusão em lista
 *     filtrada) ou quando a linha está executando.
 *
 * CHANGELOG:
 *   2026-07-09 v1.4.0 - Drag-and-drop de categorias e ações rápidas,
 *                      persistindo ordem pessoal via RPCs.
 *   2026-06-23 v1.3.0 - Botão "Nova categoria" + agrupamento por categoria
 *                      com colapso persistido em localStorage.
 *   2026-06-03 v1.2.0 - Botão "Nova ação" no header e CTA no estado vazio.
 *   2026-06-03 v1.1.0 - Botões Visualizar/Executar/Editar espelhando
 *                      ManualRoutingSection.
 *   2026-06-03 v1.0.0 - Versão inicial.
 *
 * @version 1.4.0
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Loader2,
  Play,
  Zap,
  AlertCircle,
  Search,
  Inbox,
  Eye,
  Pencil,
  Users,
  Globe,
  Plus,
  ChevronDown,
  FolderTree,
  FolderPlus,
  GripVertical,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { showToast } from '@/lib/toast';
import { createLogger } from '@/lib/logger';
import { cn } from '@/lib/utils';
import { RulePreviewDialog } from '@/components/tenant/routing/RulePreviewDialog';
import { EditRuleDialog } from '@/components/tenant/routing/EditRuleDialog';
import { CreateManualRuleDialog } from '@/components/tenant/routing/CreateManualRuleDialog';
import { RoutingCategoryIcon } from '@/components/tenant/routing/categories/RoutingCategoryIcon';
import { RoutingCategoryDialog } from '@/components/tenant/routing/categories/RoutingCategoryDialog';
import { RoutingRule } from '@/pages/tenant/RoutingRules';

// Logger dedicado ao componente
const log = createLogger('QuickActionsSheet');

// Interface da regra manual retornada pela RPC get_manual_routing_rules v1.2.0
interface ManualRule {
  id: string;
  name: string;
  description: string | null;
  created_by: string;
  visibility: string;
  is_owner: boolean;
  priority: number;
  stop_on_match: boolean;
  action_type: string;
  action_value: Record<string, unknown>;
  category_id: string | null;
  category_name: string | null;
  category_color: string | null;
  category_icon: string | null;
  category_visibility: string | null;
  category_sort_order: number | null;
  // v1.4.0: ordem pessoal por usuário (NULL quando ainda não personalizado)
  user_rule_sort_order: number | null;
  user_category_sort_order: number | null;
}

// Props do componente
interface QuickActionsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenantId: string;
  inboxId: string;
  conversationId: string;
  contactId: string;
  contactPhone: string;
  contactName?: string | null;
  replyMode?: 'direct' | 'comment_public' | 'comment_private';
  commentId?: string;
  replyToMessageId?: string;
  onExecuted: () => void;
}

// Mapeia visibilidade da regra em rótulo amigável em PT-BR
function visibilityLabel(v: string): string {
  switch (v) {
    case 'private':
      return 'Privada';
    case 'shared':
      return 'Compartilhada';
    case 'public':
      return 'Pública';
    default:
      return v;
  }
}

// Constante para chave do grupo "Sem categoria"
const UNCATEGORIZED_KEY = '__none__';

// ---------------------------------------------------------------------
// Sub-componente: linha arrastável de regra
// ---------------------------------------------------------------------
interface SortableRuleRowProps {
  rule: ManualRule;
  isRunning: boolean;
  dragDisabled: boolean;
  onPreview: (rule: ManualRule) => void;
  onExecute: (rule: ManualRule) => void;
  onEdit: (ruleId: string) => void;
}

function SortableRuleRow({
  rule,
  isRunning,
  dragDisabled,
  onPreview,
  onExecute,
  onEdit,
}: SortableRuleRowProps) {
  // Hook de item ordenável do dnd-kit
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: rule.id, disabled: dragDisabled });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'rounded-lg border bg-card p-3',
        'flex items-start gap-2',
        isDragging && 'ring-2 ring-amber-400/40 shadow-lg'
      )}
    >
      {/* Handle de drag */}
      <button
        type="button"
        className={cn(
          'flex items-center justify-center h-8 w-5 shrink-0 -ml-1',
          'text-muted-foreground/50 hover:text-muted-foreground touch-none',
          dragDisabled ? 'opacity-30 cursor-not-allowed' : 'cursor-grab active:cursor-grabbing'
        )}
        aria-label="Arrastar para reordenar ação"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>

      {/* Ícone identificador (usa o da categoria quando disponível) */}
      <div
        className="h-8 w-8 rounded-md flex items-center justify-center flex-shrink-0"
        style={{
          backgroundColor: rule.category_color
            ? `${rule.category_color}1a`
            : 'rgb(245 158 11 / 0.1)',
        }}
      >
        {rule.category_icon ? (
          <RoutingCategoryIcon
            name={rule.category_icon}
            color={rule.category_color || undefined}
            size={16}
          />
        ) : (
          <Zap className="h-4 w-4 text-amber-500" />
        )}
      </div>

      {/* Conteúdo (nome + badges + descrição) */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium truncate">{rule.name}</span>
          <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4">
            {visibilityLabel(rule.visibility)}
          </Badge>
          {!rule.is_owner && rule.visibility === 'shared' && (
            <Users className="h-3 w-3 shrink-0 text-muted-foreground" />
          )}
          {!rule.is_owner && rule.visibility === 'public' && (
            <Globe className="h-3 w-3 shrink-0 text-muted-foreground" />
          )}
        </div>
        {rule.description && (
          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
            {rule.description}
          </p>
        )}
      </div>

      {/* Controles: Olho, Play, Lápis */}
      <div className="flex items-center gap-0.5 shrink-0">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => onPreview(rule)}
            >
              <Eye className="h-4 w-4 text-muted-foreground" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Visualizar ações</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              disabled={isRunning}
              onClick={() => onExecute(rule)}
            >
              {isRunning ? (
                <Loader2 className="h-4 w-4 animate-spin text-amber-500" />
              ) : (
                <Play className="h-4 w-4 text-amber-500" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent>Executar ação</TooltipContent>
        </Tooltip>

        {rule.is_owner ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => onEdit(rule.id)}
              >
                <Pencil className="h-4 w-4 text-muted-foreground" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Editar regra</TooltipContent>
          </Tooltip>
        ) : (
          <div className="h-7 w-7" />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Estrutura de um grupo renderizado (categoria)
// ---------------------------------------------------------------------
interface CategoryGroup {
  key: string; // id da categoria ou '__none__'
  name: string;
  color: string | null;
  icon: string | null;
  effectiveSort: number; // ordem final (pessoal ?? global)
  rules: ManualRule[];
}

// ---------------------------------------------------------------------
// Sub-componente: cabeçalho arrastável de categoria
// ---------------------------------------------------------------------
interface SortableCategoryHeaderProps {
  group: CategoryGroup;
  isCollapsed: boolean;
  dragDisabled: boolean;
  onToggle: () => void;
}

function SortableCategoryHeader({
  group,
  isCollapsed,
  dragDisabled,
  onToggle,
}: SortableCategoryHeaderProps) {
  const isUncategorized = group.key === UNCATEGORIZED_KEY;
  // Uncategorized fica fixo no final e não é arrastável
  const disabled = dragDisabled || isUncategorized;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: group.key, disabled });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'w-full flex items-center gap-1 px-1 py-1.5 rounded-md',
        'hover:bg-muted/50 transition-colors',
        isDragging && 'ring-2 ring-amber-400/40'
      )}
    >
      {/* Handle de drag da categoria */}
      <button
        type="button"
        className={cn(
          'flex items-center justify-center h-5 w-4 shrink-0',
          'text-muted-foreground/50 hover:text-muted-foreground touch-none',
          disabled ? 'opacity-20 cursor-default' : 'cursor-grab active:cursor-grabbing'
        )}
        aria-label="Arrastar para reordenar categoria"
        {...attributes}
        {...listeners}
        // Impede propagação do clique/keydown para não expandir/colapsar
        onClick={(e) => e.stopPropagation()}
      >
        <GripVertical className="h-3.5 w-3.5" />
      </button>

      {/* Área clicável de colapso/expansão */}
      <button
        type="button"
        onClick={onToggle}
        className="flex-1 flex items-center gap-2 text-left min-w-0"
      >
        <ChevronDown
          className={cn(
            'h-3.5 w-3.5 text-muted-foreground transition-transform',
            isCollapsed && '-rotate-90'
          )}
        />
        <div
          className="h-5 w-5 rounded flex items-center justify-center shrink-0"
          style={{
            backgroundColor: isUncategorized ? undefined : `${group.color}20`,
          }}
        >
          {isUncategorized ? (
            <FolderTree className="h-3.5 w-3.5 text-muted-foreground" />
          ) : (
            <RoutingCategoryIcon
              name={group.icon || 'Zap'}
              color={group.color || undefined}
              size={12}
            />
          )}
        </div>
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex-1 truncate">
          {group.name}
        </span>
        <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4">
          {group.rules.length}
        </Badge>
      </button>
    </div>
  );
}

// =====================================================================
// Componente principal
// =====================================================================
export function QuickActionsSheet({
  open,
  onOpenChange,
  tenantId,
  inboxId,
  conversationId,
  contactId,
  contactPhone,
  contactName,
  replyMode,
  commentId,
  replyToMessageId,
  onExecuted,
}: QuickActionsSheetProps) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [executingRuleId, setExecutingRuleId] = useState<string | null>(null);
  const [previewRule, setPreviewRule] = useState<ManualRule | null>(null);
  const [editingRule, setEditingRule] = useState<RoutingRule | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);

  // Chave única da query para permitir invalidação após edição
  const rulesQueryKey = ['quick-action-rules', tenantId, inboxId] as const;

  // Busca as regras manuais via RPC (mesma usada pelo Popover/Sidebar)
  const { data: rules, isLoading, error } = useQuery({
    queryKey: rulesQueryKey,
    queryFn: async () => {
      log.debug('Buscando regras de ação rápida via RPC', { tenantId, inboxId });

      const { data, error } = await supabase.rpc('get_manual_routing_rules', {
        p_inbox_id: inboxId,
      });

      if (error) {
        log.error('Erro ao buscar regras via RPC', { error });
        throw error;
      }

      log.debug('Regras encontradas via RPC', { total: data?.length });
      return (data || []) as ManualRule[];
    },
    enabled: open && !!tenantId && !!inboxId,
    staleTime: 30000,
  });

  // -------------------------------------------------------------------
  // Estado local de ordem: espelha o resultado da RPC mas permite
  // reordenação otimista via drag-and-drop antes de persistir.
  // Estrutura: Map<categoryKey, string[] de rule ids na ordem escolhida>
  // e um array separado com a ordem das categorias.
  // -------------------------------------------------------------------
  const [categoryOrder, setCategoryOrder] = useState<string[]>([]);
  const [rulesOrder, setRulesOrder] = useState<Record<string, string[]>>({});

  // Reconstrói ordem local sempre que a RPC retorna novos dados
  useEffect(() => {
    if (!rules) return;

    // Deduplica categorias e resolve ordem efetiva:
    // pessoal (user_category_sort_order) > global (category_sort_order) > nome
    const catMap = new Map<
      string,
      { key: string; name: string; effective: number }
    >();

    for (const r of rules) {
      const key = r.category_id ?? UNCATEGORIZED_KEY;
      if (catMap.has(key)) continue;
      catMap.set(key, {
        key,
        name: r.category_name ?? 'Sem categoria',
        effective:
          r.user_category_sort_order ??
          r.category_sort_order ??
          Number.MAX_SAFE_INTEGER - 1,
      });
    }

    const sortedCats = Array.from(catMap.values()).sort((a, b) => {
      if (a.key === UNCATEGORIZED_KEY) return 1;
      if (b.key === UNCATEGORIZED_KEY) return -1;
      if (a.effective !== b.effective) return a.effective - b.effective;
      return a.name.localeCompare(b.name);
    });

    setCategoryOrder(sortedCats.map((c) => c.key));

    // Ordena regras dentro de cada categoria:
    // pessoal (user_rule_sort_order) > global (priority)
    const perCat: Record<string, ManualRule[]> = {};
    for (const r of rules) {
      const key = r.category_id ?? UNCATEGORIZED_KEY;
      (perCat[key] ??= []).push(r);
    }
    const nextRulesOrder: Record<string, string[]> = {};
    for (const [key, list] of Object.entries(perCat)) {
      list.sort((a, b) => {
        const sa = a.user_rule_sort_order ?? a.priority;
        const sb = b.user_rule_sort_order ?? b.priority;
        if (sa !== sb) return sa - sb;
        return a.name.localeCompare(b.name);
      });
      nextRulesOrder[key] = list.map((r) => r.id);
    }
    setRulesOrder(nextRulesOrder);
  }, [rules]);

  // Índice rápido rule.id -> ManualRule
  const rulesById = useMemo(() => {
    const map = new Map<string, ManualRule>();
    for (const r of rules ?? []) map.set(r.id, r);
    return map;
  }, [rules]);

  // Filtra por termo de busca (case insensitive)
  const filteredIds = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return null; // null = sem filtro
    const set = new Set<string>();
    for (const r of rules ?? []) {
      if (
        r.name.toLowerCase().includes(q) ||
        r.description?.toLowerCase().includes(q)
      ) {
        set.add(r.id);
      }
    }
    return set;
  }, [search, rules]);

  // Constrói grupos renderizáveis a partir do estado local + filtro
  const groupedRules = useMemo<CategoryGroup[]>(() => {
    return categoryOrder
      .map<CategoryGroup | null>((key) => {
        const ids = rulesOrder[key] ?? [];
        const visibleRules = ids
          .map((id) => rulesById.get(id))
          .filter((r): r is ManualRule => !!r)
          .filter((r) => (filteredIds ? filteredIds.has(r.id) : true));

        // Oculta categoria totalmente vazia após filtro
        if (filteredIds && visibleRules.length === 0) return null;

        const first = visibleRules[0] ?? rulesById.get(ids[0] ?? '');
        return {
          key,
          name: first?.category_name ?? 'Sem categoria',
          color: first?.category_color ?? null,
          icon: first?.category_icon ?? null,
          effectiveSort: 0,
          rules: visibleRules,
        };
      })
      .filter((g): g is CategoryGroup => !!g);
  }, [categoryOrder, rulesOrder, rulesById, filteredIds]);

  // Total de regras visíveis (para estado vazio)
  const totalVisible = groupedRules.reduce((s, g) => s + g.rules.length, 0);

  // -------------------------------------------------------------------
  // Persistência de colapso por categoria (localStorage)
  // -------------------------------------------------------------------
  const COLLAPSE_STORAGE_KEY = 'quick-actions-collapsed-categories';
  const [collapsedKeys, setCollapsedKeys] = useState<Set<string>>(() => {
    if (typeof window === 'undefined') return new Set();
    try {
      const raw = localStorage.getItem(COLLAPSE_STORAGE_KEY);
      return raw ? new Set(JSON.parse(raw) as string[]) : new Set();
    } catch {
      return new Set();
    }
  });

  const toggleCollapsed = (key: string) => {
    setCollapsedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      try {
        localStorage.setItem(
          COLLAPSE_STORAGE_KEY,
          JSON.stringify(Array.from(next))
        );
      } catch {
        // ignora falha de storage
      }
      return next;
    });
  };

  // -------------------------------------------------------------------
  // Drag-and-drop: configuração de sensores
  // -------------------------------------------------------------------
  const sensors = useSensors(
    // Distância mínima evita que cliques nos botões (olho/play/lápis)
    // sejam interpretados como início de drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Drag desabilitado durante busca (lista filtrada)
  const dragDisabled = !!search.trim();

  // -------------------------------------------------------------------
  // Persistência da ordem via RPC (fire-and-forget com rollback em erro)
  // -------------------------------------------------------------------
  const persistRuleOrder = async (categoryKey: string, orderedIds: string[]) => {
    // Ignora "Sem categoria" — regras sem categoria não têm campo pessoal
    // dedicado, mas ainda assim persistimos (a coluna aceita qualquer rule_id).
    const payload = orderedIds.map((rule_id, idx) => ({ rule_id, sort_order: idx }));
    log.debug('Persistindo ordem pessoal de regras', {
      categoryKey,
      total: payload.length,
    });
    const { error } = await supabase.rpc('upsert_user_quick_action_order', {
      p_orders: payload as unknown as never,
    });
    if (error) {
      log.error('Falha ao persistir ordem de regras', { error });
      showToast.error('Não foi possível salvar a ordem das ações');
      queryClient.invalidateQueries({ queryKey: rulesQueryKey });
    }
  };

  const persistCategoryOrder = async (orderedKeys: string[]) => {
    // Filtra "Sem categoria" (não tem id real)
    const payload = orderedKeys
      .filter((k) => k !== UNCATEGORIZED_KEY)
      .map((category_id, idx) => ({ category_id, sort_order: idx }));

    if (payload.length === 0) return;

    log.debug('Persistindo ordem pessoal de categorias', { total: payload.length });
    const { error } = await supabase.rpc(
      'upsert_user_quick_action_category_order',
      { p_orders: payload as unknown as never }
    );
    if (error) {
      log.error('Falha ao persistir ordem de categorias', { error });
      showToast.error('Não foi possível salvar a ordem das categorias');
      queryClient.invalidateQueries({ queryKey: rulesQueryKey });
    }
  };

  // Handler de drag para regras dentro de um grupo
  const handleRuleDragEnd = (categoryKey: string) => (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setRulesOrder((prev) => {
      const current = prev[categoryKey] ?? [];
      const oldIndex = current.indexOf(String(active.id));
      const newIndex = current.indexOf(String(over.id));
      if (oldIndex < 0 || newIndex < 0) return prev;
      const next = arrayMove(current, oldIndex, newIndex);
      // Persiste após atualização otimista
      void persistRuleOrder(categoryKey, next);
      return { ...prev, [categoryKey]: next };
    });
  };

  // Handler de drag para categorias
  const handleCategoryDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    // Não permite mover "Sem categoria" (fica sempre no final)
    if (active.id === UNCATEGORIZED_KEY || over.id === UNCATEGORIZED_KEY) return;
    setCategoryOrder((prev) => {
      const oldIndex = prev.indexOf(String(active.id));
      const newIndex = prev.indexOf(String(over.id));
      if (oldIndex < 0 || newIndex < 0) return prev;
      const next = arrayMove(prev, oldIndex, newIndex);
      void persistCategoryOrder(next);
      return next;
    });
  };

  // -------------------------------------------------------------------
  // Execução da regra (edge function routing-engine)
  // -------------------------------------------------------------------
  const handleExecuteRule = async (rule: ManualRule) => {
    log.info('Executando ação rápida via sheet', {
      ruleId: rule.id,
      ruleName: rule.name,
    });
    setExecutingRuleId(rule.id);

    try {
      const payload: Record<string, unknown> = {
        action: 'execute_manual',
        rule_id: rule.id,
        tenant_id: tenantId,
        inbox_id: inboxId,
        conversation_id: conversationId,
        contact_id: contactId,
        contact_phone: contactPhone,
        contact_name: contactName || undefined,
      };

      if (replyMode) payload.reply_mode = replyMode;
      if (commentId) payload.comment_id = commentId;
      if (replyToMessageId) payload.reply_to_message_id = replyToMessageId;

      const { data, error } = await supabase.functions.invoke('routing-engine', {
        body: payload,
      });

      if (error) {
        log.error('Erro na edge function', { error });
        throw error;
      }

      if (data?.success) {
        log.info('Ação executada com sucesso', {
          ruleId: rule.id,
          messagesSent: data.messages_sent,
        });

        showToast.success(
          `Ação "${rule.name}" executada!`,
          data.messages_sent > 0
            ? `${data.messages_sent} mensagem(s) enviada(s)`
            : 'Ações aplicadas com sucesso'
        );

        onOpenChange(false);
        setSearch('');
        onExecuted();
      } else {
        throw new Error(data?.error || 'Erro desconhecido ao executar ação');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro desconhecido';
      log.error('Falha ao executar ação', { ruleId: rule.id, error: errorMessage });
      showToast.error('Erro ao executar ação', errorMessage);
    } finally {
      setExecutingRuleId(null);
    }
  };

  // Carrega dados completos da regra e abre modal de edição
  const handleOpenEdit = async (ruleId: string) => {
    log.debug('Abrindo edição da regra', { ruleId });

    try {
      const [ruleRes, actionsRes] = await Promise.all([
        supabase.from('routing_rules').select('*').eq('id', ruleId).single(),
        supabase
          .from('routing_rule_actions')
          .select('id, action_type, action_value, sort_order, delay_seconds')
          .eq('rule_id', ruleId)
          .order('sort_order', { ascending: true }),
      ]);

      if (ruleRes.error) throw ruleRes.error;
      if (actionsRes.error) throw actionsRes.error;

      if (ruleRes.data) {
        setEditingRule({
          ...(ruleRes.data as Record<string, unknown>),
          actions: actionsRes.data || [],
        } as unknown as RoutingRule);
      }
    } catch (err) {
      log.error('Erro ao carregar regra para edição', { ruleId, error: err });
      showToast.error('Erro ao carregar regra');
    }
  };

  const handleEditSuccess = () => {
    setEditingRule(null);
    queryClient.invalidateQueries({ queryKey: rulesQueryKey });
  };

  const handleCreateSuccess = () => {
    setShowCreateDialog(false);
    queryClient.invalidateQueries({ queryKey: rulesQueryKey });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md p-0 flex flex-col"
      >
        {/* Cabeçalho com título e busca */}
        <SheetHeader className="px-4 py-3 border-b space-y-3">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-md bg-amber-500/10 flex items-center justify-center">
              <Zap className="h-4 w-4 text-amber-500" />
            </div>
            <div className="flex-1 min-w-0">
              <SheetTitle className="text-base text-left">Ações Rápidas</SheetTitle>
              <SheetDescription className="text-xs text-left">
                Selecione uma ação para executar nesta conversa
              </SheetDescription>
            </div>
            {/* Botão para criar nova categoria */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 sm:w-auto sm:gap-1.5 sm:px-2 text-xs text-muted-foreground hover:text-foreground shrink-0"
                  onClick={() => setShowCategoryDialog(true)}
                  aria-label="Nova categoria"
                >
                  <FolderPlus className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">Categoria</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>Criar categoria de ações</TooltipContent>
            </Tooltip>
            {/* Botão para criar nova ação rápida */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 gap-1.5 text-xs text-amber-600 hover:text-amber-700 hover:bg-amber-500/10 shrink-0"
                  onClick={() => setShowCreateDialog(true)}
                >
                  <Plus className="h-3.5 w-3.5" />
                  Nova ação
                </Button>
              </TooltipTrigger>
              <TooltipContent>Criar ação rápida</TooltipContent>
            </Tooltip>
          </div>

          {/* Campo de busca */}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar ação..."
              className="pl-8 h-9"
            />
          </div>

          {/* Dica de reordenação quando há busca ativa */}
          {dragDisabled && rules && rules.length > 1 && (
            <p className="text-[10px] text-muted-foreground -mt-1">
              Limpe a busca para reordenar categorias e ações.
            </p>
          )}
        </SheetHeader>

        {/* Lista de ações */}
        <ScrollArea className="flex-1">
          <div className="p-3 space-y-2">
            {/* Loading */}
            {isLoading && (
              <div className="flex items-center justify-center py-10 gap-2">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Carregando...</span>
              </div>
            )}

            {/* Erro */}
            {error && !isLoading && (
              <div className="flex flex-col items-center justify-center py-10 gap-2 text-destructive">
                <AlertCircle className="h-5 w-5" />
                <span className="text-sm">Erro ao carregar ações</span>
              </div>
            )}

            {/* Vazio */}
            {!isLoading && !error && totalVisible === 0 && (
              <div className="flex flex-col items-center justify-center py-12 gap-2 text-center px-4">
                <Inbox className="h-8 w-8 text-muted-foreground/60" />
                <p className="text-sm text-muted-foreground">
                  {search
                    ? `Nenhuma ação encontrada para "${search}"`
                    : 'Nenhuma ação rápida cadastrada'}
                </p>
                {!search && (
                  <Button
                    variant="link"
                    size="sm"
                    className="text-amber-500 p-0 h-auto text-xs"
                    onClick={() => setShowCreateDialog(true)}
                  >
                    Clique aqui para criar uma
                  </Button>
                )}
              </div>
            )}

            {/* Lista drag-and-drop de categorias */}
            {!isLoading && !error && totalVisible > 0 && (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleCategoryDragEnd}
              >
                <SortableContext
                  items={categoryOrder}
                  strategy={verticalListSortingStrategy}
                >
                  {groupedRules.map((group) => {
                    const isCollapsed = collapsedKeys.has(group.key);
                    return (
                      <div key={group.key} className="space-y-2">
                        {/* Cabeçalho do grupo (arrastável) */}
                        <SortableCategoryHeader
                          group={group}
                          isCollapsed={isCollapsed}
                          dragDisabled={dragDisabled}
                          onToggle={() => toggleCollapsed(group.key)}
                        />

                        {/* Itens do grupo (colapsável + drag-and-drop interno) */}
                        {!isCollapsed && group.rules.length > 0 && (
                          <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleRuleDragEnd(group.key)}
                          >
                            <SortableContext
                              items={group.rules.map((r) => r.id)}
                              strategy={verticalListSortingStrategy}
                            >
                              <div className="space-y-2">
                                {group.rules.map((rule) => (
                                  <SortableRuleRow
                                    key={rule.id}
                                    rule={rule}
                                    isRunning={executingRuleId === rule.id}
                                    dragDisabled={
                                      dragDisabled || executingRuleId === rule.id
                                    }
                                    onPreview={setPreviewRule}
                                    onExecute={handleExecuteRule}
                                    onEdit={handleOpenEdit}
                                  />
                                ))}
                              </div>
                            </SortableContext>
                          </DndContext>
                        )}
                      </div>
                    );
                  })}
                </SortableContext>
              </DndContext>
            )}
          </div>
        </ScrollArea>
      </SheetContent>

      {/* Dialog de Preview da regra */}
      {previewRule && (
        <RulePreviewDialog
          open={!!previewRule}
          onOpenChange={(o) => !o && setPreviewRule(null)}
          ruleId={previewRule.id}
          ruleName={previewRule.name}
          ruleDescription={previewRule.description}
        />
      )}

      {/* Dialog de Edição da regra */}
      {editingRule && (
        <EditRuleDialog
          open={!!editingRule}
          onOpenChange={(o) => !o && setEditingRule(null)}
          rule={editingRule}
          onSuccess={handleEditSuccess}
        />
      )}

      {/* Dialog de Criação de Ação Rápida (reutilizado do sidebar) */}
      <CreateManualRuleDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onSuccess={handleCreateSuccess}
      />

      {/* Dialog de Criação de Categoria - permite criar sem sair do Sheet */}
      <RoutingCategoryDialog
        open={showCategoryDialog}
        onOpenChange={setShowCategoryDialog}
      />
    </Sheet>
  );
}
